import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SelectOption } from './MultiSelect';
import './PeriodRangePicker.css';

export type PeriodRangeGrain = 'day' | 'week' | 'month' | 'quarter' | 'halfYear' | 'year';

interface PeriodRangePickerProps {
  grain: PeriodRangeGrain;
  start: string;
  end: string;
  max: string;
  options: readonly SelectOption[];
  formatValue: (value: string) => string;
  normalizeValue: (value: string) => string;
  onChange: (range: { start: string; end: string }) => void;
}

interface MonthCursor {
  year: number;
  month: number;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const WEEKDAYS_MONDAY_FIRST = ['一', '二', '三', '四', '五', '六', '日'];

function parseYmd(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function formatYmd(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

// The picker stores normalized period keys (for example, a month always uses
// its first day) but report APIs expect an inclusive calendar-date range. Keep
// those two representations separate so selecting one month is not sent as
// just its first day.
function periodEnd(value: string, grain: PeriodRangeGrain, max: string) {
  const date = parseYmd(value);
  if (!date) return value;

  switch (grain) {
    case 'week':
      date.setUTCDate(date.getUTCDate() + 6);
      break;
    case 'month':
      date.setUTCMonth(date.getUTCMonth() + 1, 0);
      break;
    case 'quarter':
      date.setUTCMonth(date.getUTCMonth() + 3, 0);
      break;
    case 'halfYear':
      date.setUTCMonth(date.getUTCMonth() + 6, 0);
      break;
    case 'year':
      date.setUTCMonth(11, 31);
      break;
    default:
      break;
  }

  const end = formatYmd(date);
  return max && end > max ? max : end;
}

function monthFor(value: string): MonthCursor {
  const date = parseYmd(value) ?? new Date();
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
}

function moveMonth(cursor: MonthCursor, amount: number): MonthCursor {
  const date = new Date(Date.UTC(cursor.year, cursor.month + amount, 1));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
}

function calendarDays(cursor: MonthCursor, mondayFirst = false) {
  const first = new Date(Date.UTC(cursor.year, cursor.month, 1));
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - (mondayFirst ? (start.getUTCDay() + 6) % 7 : start.getUTCDay()));
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setUTCDate(start.getUTCDate() + index);
    return {
      value: formatYmd(date),
      label: String(date.getUTCDate()),
      isCurrentMonth: date.getUTCMonth() === cursor.month,
    };
  });
}

export function PeriodRangePicker({ grain, start, end, max, options, formatValue, normalizeValue, onChange }: PeriodRangePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(() => normalizeValue(start));
  const [draftEnd, setDraftEnd] = useState(() => normalizeValue(end));
  const [cursor, setCursor] = useState<MonthCursor>(() => monthFor(start || end || max));
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const orderedOptions = useMemo(() => [...options].sort((a, b) => a.value.localeCompare(b.value)), [options]);
  const selectValue = useCallback((value: string) => {
    if (!draftStart || draftEnd) {
      setDraftStart(value);
      setDraftEnd('');
      return;
    }
    if (value < draftStart) {
      setDraftStart(value);
      setDraftEnd(draftStart);
      return;
    }
    setDraftEnd(value);
  }, [draftEnd, draftStart]);

  const updatePosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = grain === 'day' || grain === 'week' ? 650 : 520;
    const bottom = window.innerHeight - rect.bottom;
    const dropdownHeight = grain === 'day' || grain === 'week' ? 390 : 360;
    setPosition({
      top: bottom >= dropdownHeight || rect.top < dropdownHeight ? rect.bottom + 6 : Math.max(8, rect.top - dropdownHeight - 6),
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    });
  }, [grain]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    updatePosition();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  const openPicker = () => {
    const normalizedStart = normalizeValue(start);
    const normalizedEnd = normalizeValue(end);
    setDraftStart(normalizedStart);
    setDraftEnd(normalizedEnd);
    setCursor(monthFor(normalizedStart || normalizedEnd || max));
    setOpen(true);
  };

  const chooseCalendarDay = (value: string) => {
    if (value > max) return;
    selectValue(normalizeValue(value));
  };

  const isInDraftRange = (value: string) => Boolean(draftStart && draftEnd && value >= draftStart && value <= draftEnd);
  const normalizedStart = normalizeValue(start);
  const normalizedEnd = normalizeValue(end);
  const compactTriggerLabel = (value: string) => {
    const label = formatValue(value);
    // 周的完整标签含“周一至周日”两段日期，放在筛选栏会把整行撑出视口；完整信息仍在弹层摘要中保留。
    return grain === 'week' ? label.replace(/（.*$/, '') : label;
  };
  const pickerLabel = normalizedStart && normalizedEnd ? `${compactTriggerLabel(normalizedStart)} 至 ${compactTriggerLabel(normalizedEnd)}` : `选择${grain === 'day' ? '日期' : '周期'}范围`;
  const fullPickerLabel = normalizedStart && normalizedEnd ? `${formatValue(normalizedStart)} 至 ${formatValue(normalizedEnd)}` : pickerLabel;
  const activeLabel = draftStart && !draftEnd ? `已选开始：${formatValue(draftStart)}，请选择结束` : draftStart && draftEnd ? `${formatValue(draftStart)} 至 ${formatValue(draftEnd)}` : '请选择起止范围';
  const renderMonth = (month: MonthCursor) => (
    <div className="period-range-month" key={`${month.year}-${month.month}`}>
      <div className="period-range-month-title">{month.year} 年 {month.month + 1} 月</div>
      <div className="period-range-weekdays">{(grain === 'week' ? WEEKDAYS_MONDAY_FIRST : WEEKDAYS).map(day => <span key={day}>{day}</span>)}</div>
      {grain === 'week' ? (
        <div className="period-range-week-grid">
          {Array.from({ length: 6 }, (_, weekIndex) => {
            const days = calendarDays(month, true).slice(weekIndex * 7, weekIndex * 7 + 7);
            const periodValue = normalizeValue(days[0].value);
            const selected = periodValue === draftStart || periodValue === draftEnd;
            const inRange = isInDraftRange(periodValue);
            const disabled = periodValue > normalizeValue(max);
            return <button
              type="button"
              key={periodValue}
              disabled={disabled}
              onClick={() => chooseCalendarDay(days[0].value)}
              className={`period-range-week${selected ? ' selected' : ''}${inRange ? ' in-range' : ''}`}
              aria-label={formatValue(periodValue)}
            >{days.map(day => <span key={day.value} className={day.isCurrentMonth ? '' : 'outside'}>{day.label}</span>)}</button>;
          })}
        </div>
      ) : (
        <div className="period-range-calendar-grid">
          {calendarDays(month).map(day => {
          const periodValue = normalizeValue(day.value);
          const selected = periodValue === draftStart || periodValue === draftEnd;
          const inRange = isInDraftRange(periodValue);
          const disabled = day.value > max;
          return <button
            type="button"
            key={day.value}
            disabled={disabled}
            onClick={() => chooseCalendarDay(day.value)}
            className={`period-range-day${day.isCurrentMonth ? '' : ' outside'}${selected ? ' selected' : ''}${inRange ? ' in-range' : ''}`}
          >{day.label}</button>;
          })}
        </div>
      )}
    </div>
  );

  return <div className="period-range-picker" ref={rootRef}>
    <button type="button" className={`period-range-trigger${open ? ' open' : ''}`} onClick={() => open ? setOpen(false) : openPicker()}>
      <CalendarDays size={15} />
      <span title={fullPickerLabel}>{pickerLabel}</span>
    </button>
    {open && position && createPortal(
      <div ref={dropdownRef} className="period-range-dropdown" style={{ top: position.top, left: position.left }}>
        <div className="period-range-summary">{activeLabel}</div>
        {grain === 'day' || grain === 'week' ? (
          <div className="period-range-calendar">
            <button type="button" className="period-range-nav previous" onClick={() => setCursor(current => moveMonth(current, -1))} aria-label="上个月"><ChevronLeft size={18} /></button>
            <div className="period-range-months">{renderMonth(cursor)}{renderMonth(moveMonth(cursor, 1))}</div>
            <button type="button" className="period-range-nav next" onClick={() => setCursor(current => moveMonth(current, 1))} aria-label="下个月"><ChevronRight size={18} /></button>
          </div>
        ) : (
          <div className="period-range-option-grid">
            {orderedOptions.map(option => {
              const selected = option.value === draftStart || option.value === draftEnd;
              const inRange = isInDraftRange(option.value);
              return <button type="button" key={option.value} onClick={() => selectValue(option.value)} className={`period-range-option${selected ? ' selected' : ''}${inRange ? ' in-range' : ''}`}>{option.label}</button>;
            })}
          </div>
        )}
        <div className="period-range-actions">
          <button type="button" className="btn btn-text btn-sm" onClick={() => { setDraftStart(''); setDraftEnd(''); }}>清空</button>
          <button type="button" className="btn btn-primary btn-sm" disabled={!draftStart || !draftEnd} onClick={() => { onChange({ start: draftStart, end: periodEnd(draftEnd, grain, max) }); setOpen(false); }}>确定</button>
        </div>
      </div>,
      document.body,
    )}
  </div>;
}

export default PeriodRangePicker;
